import React from "react";
import { DateTime } from "luxon";
import FetchHelper from "./lib/fetchHelper.js";
import { setAppBadge } from "./lib/pushNotifications";
import DateUtility from "./lib/dateUtility";
import { ReactRenderer } from "./lib/ReactRenderer";
import { scrollBehavior } from "./lib/motion";
import { linkifyText } from "./lib/linkify";
export const INBOX_READ_EVENT = "bp-inbox-read";
const ICON_PROPS = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true
};
/**
 * The member's messages from the last N days, whatever channel delivered them. Messages shown
 * unread are marked read on load, but keep their highlight until the next visit.
 */
export class MemberInboxComponent extends React.Component {
    constructor(props) {
        super(props);
        const messageId = Number(new URLSearchParams(window.location.search).get("messageId"));
        this.state = {
            loading: true,
            retentionDays: 14,
            messages: [],
            highlightId: Number.isFinite(messageId) && messageId > 0 ? messageId : undefined
        };
    }
    componentDidMount() {
        FetchHelper.get("/bpapi/memberinbox")
            .then((result) => {
            this.setState({ loading: false, retentionDays: result.retentionDays, messages: result.messages }, () => this.afterLoad());
        })
            .catch(() => this.setState({ loading: false, error: "Kunne ikke hente dine beskeder" }));
    }
    afterLoad() {
        var _a;
        const unread = this.state.messages.filter((m) => !m.read).map((m) => m.id);
        if (unread.length > 0) {
            FetchHelper.post("/bpapi/memberinbox/markread", { ids: unread })
                .then((result) => {
                setAppBadge(result.count);
                window.dispatchEvent(new CustomEvent(INBOX_READ_EVENT, { detail: result.count }));
            })
                .catch(() => undefined);
        }
        else {
            setAppBadge(0);
        }
        if (this.state.highlightId) {
            (_a = document.getElementById(`inbox-message-${this.state.highlightId}`)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: scrollBehavior(), block: "center" });
            window.setTimeout(() => this.setState({ highlightId: undefined }), 3000);
        }
    }
    renderMessage(message) {
        var _a, _b;
        const sent = DateTime.fromISO(message.sentAtUtc, { zone: "utc" }).setZone("Europe/Copenhagen");
        const classes = ["bi-ib-message"];
        if (!message.read)
            classes.push("bi-ib-unread");
        if (message.id === this.state.highlightId)
            classes.push("bi-ib-hit");
        const isShiftLink = !!message.url && message.url.startsWith("/vagtinfo");
        // A message whose text already links to the page, such as a swap offer, needs no button.
        const linkedInText = !!message.url && ((_a = message.body) !== null && _a !== void 0 ? _a : "").includes(message.url);
        return (React.createElement("li", { key: message.id, id: `inbox-message-${message.id}`, className: classes.join(" ") },
            React.createElement("div", { className: "bi-ib-top" },
                React.createElement("strong", null, message.title),
                React.createElement("span", { className: "bi-ib-channels" },
                    message.smsSent
                        ? React.createElement("span", { role: "img", "aria-label": "Sendt som SMS", title: "Sendt som SMS" },
                            React.createElement("svg", Object.assign({}, ICON_PROPS),
                                React.createElement("path", { d: "M4 5h16v11H9l-5 4z" })))
                        : null,
                    message.pushSent
                        ? React.createElement("span", { role: "img", "aria-label": "Sendt som notifikation", title: "Sendt som notifikation" },
                            React.createElement("svg", Object.assign({}, ICON_PROPS),
                                React.createElement("path", { d: "M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" }),
                                React.createElement("path", { d: "M10 21h4" })))
                        : null)),
            React.createElement("span", { className: "bi-ib-time" },
                "kl. ",
                sent.toFormat("HH:mm")),
            React.createElement("span", { className: "bi-ib-body", dangerouslySetInnerHTML: { __html: linkifyText((_b = message.body) !== null && _b !== void 0 ? _b : "") } }),
            !!message.url && message.url.startsWith("/") && !message.url.startsWith("/min-side/indbakke") && !linkedInText
                ? React.createElement("a", { className: "bi-ib-open", href: message.url }, isShiftLink ? "Se vagten" : "Åbn")
                : null));
    }
    /** The messages in one card, newest first, each day under a bar of its own. */
    renderMessages(messages) {
        const now = DateTime.now().setZone("Europe/Copenhagen");
        const rows = [];
        let last = "";
        messages.forEach((message) => {
            const sent = DateTime.fromISO(message.sentAtUtc, { zone: "utc" }).setZone("Europe/Copenhagen");
            const day = DateUtility.messageDay(sent, now);
            if (day !== last) {
                rows.push(React.createElement("li", { className: "bi-ib-day", key: `day-${message.id}` }, day));
                last = day;
            }
            rows.push(this.renderMessage(message));
        });
        return rows;
    }
    render() {
        const { loading, error, messages, retentionDays } = this.state;
        return (React.createElement("div", { className: "bi-ib" },
            React.createElement("div", { className: "bi-ib-head" },
                React.createElement("h1", null, "Indbakke"),
                React.createElement("p", null,
                    "Dine beskeder fra de seneste ",
                    retentionDays,
                    " dage.")),
            loading ? React.createElement("div", { className: "bp-loading" },
                React.createElement("i", { className: "fa fa-spinner fa-pulse fa-fw" })) : null,
            error ? React.createElement("div", { className: "alert alert-danger" }, error) : null,
            !loading && !error && messages.length === 0
                ? React.createElement("div", { className: "bi-ib-card bi-ib-empty" },
                    React.createElement("svg", Object.assign({}, ICON_PROPS, { strokeWidth: 1.5 }),
                        React.createElement("path", { d: "M3 13h5l1.5 3h5L16 13h5" }),
                        React.createElement("path", { d: "M5 5h14l2 8v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5z" })),
                    React.createElement("p", null,
                        "Ingen beskeder de seneste ",
                        retentionDays,
                        " dage"))
                : null,
            messages.length > 0
                ? React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "bi-ib-card" },
                        React.createElement("ul", null, this.renderMessages(messages))),
                    React.createElement("p", { className: "bi-ib-note" },
                        "Beskeder gemmes i ",
                        retentionDays,
                        " dage."))
                : null));
    }
}
ReactRenderer.render("memberinboxcomponent", (elem) => React.createElement(MemberInboxComponent, null));
